import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { Box, Button, TextField, Typography, CircularProgress, Alert, Paper, Backdrop } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import CameraModal from '../camera/CameraModal';
import { useFuelReceiptAI } from '../../hooks/useFuelReceiptAI';
import { fuelConsumptionService } from '../../services/fuelConsumptionService';
import { ExtractedFuelData, FuelConsumptionInput } from '../../types/fuelConsumption';
import { Vehicle } from '../../types/vehicle';

interface FuelReceiptEntryProps {
  vehicle: Vehicle;
  organizationId: string;
  onSuccess: () => void;
  onCancel: () => void;
}

export const FuelReceiptEntry: React.FC<FuelReceiptEntryProps> = ({
  vehicle,
  organizationId,
  onSuccess,
  onCancel
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { analyzeReceipt, validateExtractedData, isAnalyzing, error: aiError, clearError } = useFuelReceiptAI();
  const [error, setError] = useState<string | null>(null);
  
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitSuccess, setIsSubmitSuccess] = useState(false);
  
  const [formData, setFormData] = useState<Omit<FuelConsumptionInput, 'receiptUrl'>>({
    vehicleId: vehicle.id,
    organizationId,
    date: new Date(),
    amount: 0,
    volume: 0,
    supplier: ''
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Mettre à jour le vehicleId quand le véhicule change
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      vehicleId: vehicle.id,
      organizationId
    }));
  }, [vehicle.id, organizationId]);

  const resetForm = () => {
    setFormData({
      vehicleId: vehicle.id,
      organizationId,
      date: new Date(),
      amount: 0,
      volume: 0,
      supplier: ''
    });
    setReceiptFile(null);
    setError(null);
    clearError?.();
  };

  const optimizeImage = async (file: File): Promise<File> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          // Dimensions maximales
          const maxWidth = 1600;
          const maxHeight = 1600;
          let width = img.width;
          let height = img.height;

          // Redimensionner si nécessaire
          if (width > height && width > maxWidth) {
            height = Math.round((height * maxWidth) / width);
            width = maxWidth;
          } else if (height > maxHeight) {
            width = Math.round((width * maxHeight) / height);
            height = maxHeight;
          }

          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');

          if (!ctx) {
            reject(new Error('Could not get canvas context'));
            return;
          }

          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.clearRect(0, 0, width, height);
          ctx.drawImage(img, 0, 0, width, height);

          // Convertir en JPEG avec une qualité optimisée
          canvas.toBlob(
            (blob) => {
              if (blob) {
                if (blob.size > 5 * 1024 * 1024) {
                  // Si plus de 5MB, réduire la qualité
                  canvas.toBlob(
                    (reducedBlob) => {
                      if (reducedBlob) {
                        resolve(new File([reducedBlob], file.name, { type: 'image/jpeg' }));
                      } else {
                        reject(new Error('Failed to create reduced blob'));
                      }
                    },
                    'image/jpeg',
                    0.7
                  );
                } else {
                  resolve(new File([blob], file.name, { type: 'image/jpeg' }));
                }
              } else {
                reject(new Error('Failed to create blob'));
              }
            },
            'image/jpeg',
            0.9
          );
        };
        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = e.target?.result as string;
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsDataURL(file);
    });
  };

  const handlePhotoTaken = async (file: File) => {
    try {
      const optimizedFile = await optimizeImage(file);
      setReceiptFile(optimizedFile);
      
      // Convert optimized file to base64
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64String = reader.result as string;
        const imageBase64 = base64String.split(',')[1];
        
        console.log('Processing optimized image:', {
          fileSize: optimizedFile.size,
          fileType: optimizedFile.type,
          base64Length: imageBase64.length
        });
        
        const extractedData = await analyzeReceipt(imageBase64);
        console.log('Extracted data:', extractedData);
        if (extractedData && validateExtractedData(extractedData)) {
          updateFormWithExtractedData(extractedData);
        }
      };
      reader.readAsDataURL(optimizedFile);
    } catch (err) {
      console.error('Error optimizing image:', err);
      setError(t('fuelReceipt.errors.imageProcessing'));
    }
  };

  const updateFormWithExtractedData = (data: ExtractedFuelData) => {
    setFormData(prev => ({
      ...prev,
      ...(data.date && { date: data.date }),
      ...(data.amount && { amount: data.amount }),
      ...(data.volume && { volume: data.volume }),
      ...(data.supplier && { supplier: data.supplier })
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    setError(null);

    try {
      if (!user?.id) {
        throw new Error(t('auth.errors.notAuthenticated'));
      }

      // Validation des données
      if (!formData.vehicleId || !formData.organizationId) {
        throw new Error(t('fuelReceipt.errors.missingData'));
      }

      if (formData.amount <= 0 || formData.volume <= 0) {
        throw new Error(t('fuelReceipt.errors.invalidAmount'));
      }

      if (!formData.supplier.trim()) {
        throw new Error(t('fuelReceipt.errors.missingSupplier'));
      }

      console.log('Submitting fuel consumption data:', {
        ...formData,
        userId: user.id,
        hasReceiptFile: !!receiptFile
      });

      await fuelConsumptionService.create(formData, user.id, receiptFile || undefined);
      console.log('Fuel consumption created successfully');
      setSuccessMessage(t('fuelReceipt.success.saved'));
      setIsSubmitSuccess(true);
      resetForm();
      // Ne pas appeler onSuccess immédiatement pour permettre à l'utilisateur d'ajouter un autre ticket
    } catch (err) {
      console.error('Error creating fuel consumption:', err);
      const errorMessage = err instanceof Error ? err.message : t('fuelReceipt.errors.saveFailed');
      setSuccessMessage(null);
      setError(errorMessage);
      setIsSubmitSuccess(false);
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddAnother = () => {
    setIsSubmitSuccess(false);
    setSuccessMessage(null);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2, position: 'relative' }}>
      <Typography variant="h6" gutterBottom>
        {t('fuelReceipt.title')}
      </Typography>

      {(error || aiError) && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || aiError}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      
      {/* Overlay de chargement pour l'analyse IA */}
      {isAnalyzing && (
        <Backdrop
          sx={{
            position: 'absolute',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            width: '100%',
            height: '100%',
            borderRadius: 1
          }}
          open={true}
        >
          <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
          <Paper elevation={3} sx={{ p: 2, backgroundColor: 'primary.light', color: 'white' }}>
            <Typography variant="body1" fontWeight="medium">
              {t('fuelReceipt.analyzing', 'Analyse du ticket en cours...')}
            </Typography>
          </Paper>
        </Backdrop>
      )}

      {isSubmitSuccess ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAddAnother}
            size="large"
          >
            {t('fuelReceipt.buttons.addAnother')}
          </Button>
          <Button
            variant="outlined"
            color="primary"
            onClick={onSuccess}
            size="large"
            sx={{ ml: 2 }}
          >
            {t('common.back')}
          </Button>
        </Box>
      ) : (
        <>
          <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
            <Button
              variant="contained"
              onClick={() => setIsCameraOpen(true)}
              disabled={isAnalyzing || isSaving}
            >
              {t('fuelReceipt.form.takePicture')}
            </Button>
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              id="upload-receipt"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handlePhotoTaken(file);
                }
                // Reset input value to allow selecting the same file again
                e.target.value = '';
              }}
            />
            <Button
              variant="outlined"
              component="label"
              htmlFor="upload-receipt"
              disabled={isAnalyzing || isSaving}
            >
              {t('fuelReceipt.form.uploadReceipt')}
            </Button>
          </Box>

          <DatePicker
            label={t('fuelReceipt.form.date')}
            value={formData.date}
            onChange={(newValue) => {
              if (newValue) {
                setFormData(prev => ({ ...prev, date: newValue }));
              }
            }}
            sx={{ mb: 2, width: '100%' }}
          />

          <TextField
            fullWidth
            label={t('fuelReceipt.form.amount')}
            type="number"
            value={formData.amount}
            onChange={(e) => setFormData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
            margin="normal"
            inputProps={{ step: "0.01" }}
          />

          <TextField
            fullWidth
            label={t('fuelReceipt.form.volume')}
            type="number"
            value={formData.volume}
            onChange={(e) => setFormData(prev => ({ ...prev, volume: parseFloat(e.target.value) || 0 }))}
            margin="normal"
            inputProps={{ step: "0.01" }}
          />

          <TextField
            fullWidth
            label={t('fuelReceipt.form.supplier')}
            value={formData.supplier}
            onChange={(e) => setFormData(prev => ({ ...prev, supplier: e.target.value }))}
            margin="normal"
          />

          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={onCancel} disabled={isSaving}>
              {t('fuelReceipt.form.cancel')}
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSaving}
              startIcon={isSaving ? <CircularProgress size={20} /> : null}
            >
              {t('fuelReceipt.form.submit')}
            </Button>
          </Box>
        </>
      )}

      <CameraModal
        open={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onPhotoTaken={handlePhotoTaken}
      />
    </Box>
  );
};
